import { canonicalizeLegacyPermalinkPath } from '#pplx/frontend/apps/ask/permalink-slug/canonicalizeLegacyPermalink.js';
import { SearchMode } from '#pplx/frontend/ask-models/types/search-modes.js';
import { citationRegex } from '#pplx/frontend/components/search/markdown/utils/citations.js';
import { normalizeTimestamp } from '#pplx/frontend/i18n/timestamps.js';
function normalizeImageGenerationModel(model) {
    return model ?? 'default';
}
export function getPageUrl(articleSlug, topicSlug) {
    if (articleSlug.startsWith('http')) {
        return articleSlug;
    }
    return topicSlug
        ? `/discover/${topicSlug}/${articleSlug}`
        : canonicalizeLegacyPermalinkPath(`/page/${articleSlug}`);
}
export const ASI_ROUTE_OVERRIDE = {
    ComputerTasks: 'computer-tasks',
};
const ASI_ROUTE_OVERRIDE_BASE_PATH = {
    [ASI_ROUTE_OVERRIDE.ComputerTasks]: '/computer/tasks',
};
function getAsiRouteOverride(options) {
    if (!options || typeof options !== 'object')
        return undefined;
    return options.asiRouteOverride;
}
export function transformThreadResult(threadResult, options) {
    const asiRouteOverride = getAsiRouteOverride(options);
    const route = (() => {
        if (threadResult.mode === SearchMode.ASI) {
            if (asiRouteOverride) {
                return ASI_ROUTE_OVERRIDE_BASE_PATH[asiRouteOverride];
            }
            return '/computer/tasks';
        }
        switch (threadResult.mode) {
            case 'article':
                return '/page';
            default:
                return '/search';
        }
    })();
    const href = canonicalizeLegacyPermalinkPath(`${route}/${threadResult.slug}`);
    return {
        title: threadResult.title ?? '',
        preview: threadResult.answer_preview ?? null,
        threadCount: threadResult.query_count,
        lastUpdated: normalizeTimestamp(threadResult.last_query_datetime),
        href: threadResult.matched_entry_uuid ? `${href}#${threadResult.matched_entry_uuid}` : href,
        rwToken: threadResult.read_write_token,
        focus: threadResult.search_focus ?? 'internet',
        entryUUID: threadResult.uuid,
        contextUUID: threadResult.context_uuid,
        copilot: threadResult.mode === 'copilot',
        socialInfo: threadResult.social_info,
        collectionUUID: threadResult.collection?.uuid ?? null,
        authorImage: threadResult.author_image ?? null,
        authorName: threadResult.author_name ?? null,
        authorUsername: threadResult.author_username ?? null,
        featuredImage: threadResult?.featured_images?.[0]?.image ?? null,
        thumbnailImage: threadResult?.featured_images?.[0]?.thumbnail ?? null,
        threadAccess: threadResult.thread_access,
        collection: threadResult.collection,
        mode: threadResult.mode,
        expiry: threadResult.expiry_time,
        source: threadResult?.source ?? null,
        displayModel: threadResult.display_model,
        slug: threadResult.slug,
        threadStatus: threadResult.thread_status,
        threadStatusSummary: threadResult.thread_status_summary,
        threadStatusSummaryEnum: threadResult.thread_status_summary_enum,
        latestElapsedTime: threadResult.latest_elapsed_time,
        wakeAt: threadResult.wake_at,
        crons: threadResult.crons,
        workflowSnapshots: threadResult.workflow_snapshots,
        subscribeEntryUuids: threadResult.subscribe_entry_uuids?.slice() ?? null,
        assets: threadResult.assets
            ? threadResult.assets
            : null,
        attachments: threadResult.attachments ?? null,
        unread: threadResult.unread ?? false,
        isPinned: threadResult.is_pinned ?? false,
    };
}
export const transformArticleResult = (article, topicSlug) => {
    return {
        title: article.title ?? '',
        summary: article.summary ?? '',
        description: article.description ?? '',
        href: getPageUrl(article.slug, topicSlug),
        backendUUID: article.uuid,
        collectionUUID: article.collection?.uuid ?? null,
        authorImage: article.author_image ?? null,
        authorUsername: article.author_username ?? null,
        featuredImage: article?.featured_images?.[0]?.image ?? null,
        collection: article.collection,
        socialInfo: article.social_info,
        feedbackState: article.feedback_state,
        supportedFeedbacks: article.supported_feedbacks,
        accessLevel: article.access ?? article.thread_access,
        preview: article.description.replace(citationRegex, ''),
        offset: article.hero_offset,
        entryUUID: article.uuid,
        rwToken: article.read_write_token,
        lastUpdated: article.last_query_datetime,
        threadAccess: article.access,
        thumbnailImage: article?.featured_images?.[0]?.thumbnail ?? null,
        contextUUID: article.context_uuid,
        webResults: article.web_results_preview,
        feedUUID: article.feed_uuid,
        datavizUrl: article.dataviz_url,
        datavizSignedUrl: article.dataviz_signed_url,
    };
};
export const settingsMapping = {
    gpt4_limit: 'gpt4Limit',
    opus_limit: 'opusLimit',
    pages_limit: 'pagesLimit',
    upload_limit: 'uploadLimit',
    article_image_upload_limit: 'articleImageUploadLimit',
    max_files_per_user: 'maxFilesPerUser',
    max_files_per_repository: 'maxFilesPerRepository',
    default_image_generation_model: 'defaultImageGenerationModel',
    default_video_generation_model: 'defaultVideoGenerationModel',
    query_count: 'queryCount',
    query_count_copilot: 'queryCountCopilot',
    query_count_mobile: 'queryCountMobile',
    has_ai_profile: 'hasAiProfile',
    disable_training: 'disableTraining',
    is_sidebar_collapsed: 'isSidebarCollapsed',
    is_sidebar_pinned: 'isSidebarPinned',
    sidebar_hidden_hubs: 'sidebarHiddenHubs',
    subscription_tier: 'subscriptionTier',
    create_limit: 'createLimit',
    notif_status: 'notifStatus',
    email_status: 'emailStatus',
    allow_article_creation: 'allowArticleCreation',
    is_verified: 'isVerified',
    always_allow_browser_agent: 'alwaysAllowBrowserAgent',
    always_use_extended_context: 'alwaysUseExtendedContext',
    inline_visualizations_enabled: 'inlineVisualizationsEnabled',
    viewer_analytics_enabled: 'viewerAnalyticsEnabled',
    computer_queue_followups: 'computerQueueFollowups',
    primary_teammate_context_uuid: 'primaryTeammateContextUuid',
    has_accepted_api_terms: 'hasAcceptedApiTerms',
    default_homepage_tab: 'defaultHomepageTab',
    release_phase: 'releasePhase',
};
export const DEFAULT_IS_SIDEBAR_PINNED = true;
function parseConnectorsData(data) {
    if (!data)
        return { connectors: [] };
    return data;
}
export const parseSettings = (settings) => {
    return {
        hasLoadedSettings: true,
        pagesLimit: settings?.pages_limit ?? 0,
        uploadLimit: settings?.upload_limit,
        defaultImageGenerationModel: normalizeImageGenerationModel(settings?.default_image_generation_model),
        defaultVideoGenerationModel: settings?.default_video_generation_model ?? 'default',
        articleImageUploadLimit: settings?.article_image_upload_limit ?? 0,
        maxFilesPerUser: settings?.max_files_per_user ?? 0,
        maxFilesPerRepository: settings?.max_files_per_repository ?? 0,
        queryCount: settings?.query_count ?? 0,
        queryCountCopilot: settings?.query_count_copilot ?? 0,
        queryCountMobile: settings?.query_count_mobile ?? 0,
        hasAiProfile: settings?.has_ai_profile ?? false,
        disableTraining: settings?.disable_training ?? false,
        isSidebarCollapsed: settings?.is_sidebar_collapsed ?? false,
        isSidebarPinned: settings?.is_sidebar_pinned ?? DEFAULT_IS_SIDEBAR_PINNED,
        sidebarHiddenHubs: settings?.sidebar_hidden_hubs ?? [],
        subscriptionTier: settings?.subscription_tier ?? settings?.subscription_tier ?? 'null',
        stripeStatus: settings?.stripe_status ?? 'none',
        revenuecatStatus: settings?.revenuecat_status ?? 'none',
        revenuecatSource: settings?.revenuecat_source ?? 'none',
        createLimit: settings?.create_limit ?? 0,
        notifStatus: settings?.notif_status ?? 'daily',
        emailStatus: settings?.email_status ?? 'daily',
        hasDataRetentionWarning: settings?.has_data_retention_warning ?? false,
        allowArticleCreation: settings?.allow_article_creation ?? false,
        isVerified: settings?.is_verified ?? false,
        connectors: parseConnectorsData(settings?.connectors),
        connectorLimits: settings?.connector_limits ?? {
            global_file_count: undefined,
            repo_type_limits: undefined,
            max_file_size_mb: undefined,
            max_attachment_file_size_mb: undefined,
            max_attachment_file_size_mb_by_source: undefined,
        },
        alwaysAllowBrowserAgent: settings?.always_allow_browser_agent ?? false,
        alwaysUseExtendedContext: settings?.always_use_extended_context ?? false,
        inlineVisualizationsEnabled: settings?.inline_visualizations_enabled ?? true,
        viewerAnalyticsEnabled: settings?.viewer_analytics_enabled ?? true,
        computerQueueFollowups: settings?.computer_queue_followups ?? false,
        primaryTeammateContextUuid: settings?.primary_teammate_context_uuid ?? null,
        viewerAnalyticsControlAvailable: settings?.viewer_analytics_control_available ?? false,
        viewerAnalyticsDisabledByOrganization: settings?.viewer_analytics_disabled_by_organization ?? false,
        hasAcceptedApiTerms: settings?.has_accepted_api_terms ?? false,
        defaultHomepageTab: settings?.default_homepage_tab ?? null,
        effectiveDefaultAskInputMode: settings?.effective_default_ask_input_mode ?? 'search',
        organizationDefaultAskInputMode: settings?.organization_default_ask_input_mode ?? null,
        sources: settings?.sources ?? {
            source_to_limit: {},
        },
        releasePhase: settings?.release_phase ?? 'beta',
    };
};
export function mergeWidgetBlocks(oldBlock, newBlock) {
    if (!oldBlock) {
        return newBlock;
    }
    if (oldBlock.place_widget_block && newBlock.place_widget_block) {
        // only review_summary is updated between old and new blocks
        return {
            ...newBlock,
            place_widget_block: {
                ...oldBlock.place_widget_block,
                review_summary: newBlock.place_widget_block.review_summary,
            },
        };
    }
    return newBlock;
}
//# sourceMappingURL=dataTransformation.js.map